import styled from '@emotion/styled';
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';

import { ChatBlkQueryService } from '../services';
import { getStore } from '../utils';

const ServerMessageImageModal = styled.div`
    position: fixed;
    top: var(--header--height, 0);
    left: 0;
    background-color: rgba(0, 0, 0, 0.7);
    display: flex;
    width: 100%;
    height: 100%;
    justify-content: center;
    align-items: center;
    z-index: 2000;
    opacity: 0;
    transition: opacity 1s ease-in-out;

    &.active {
        opacity: 1;
    }
`;

const ExpandedServerMessageImageContainer = styled.div`
    max-width: 80%;
    max-height: 80%;
    width: auto;
    display: flex;
    justify-content: center;
`;

const ExpandedServerMessageImage = styled.img`
    height: 100%;
    width: 100%;
`;

export interface ImageConfig {
    src: string;
    alt: string;
}

export const ImageModalContext =
    createContext<(imgConfig: ImageConfig) => void>(undefined);

export const useImageModalContext = () => useContext(ImageModalContext);

interface ImageModalProviderProps {
    children?: JSX.Element;
}

/**
 * Provides a context for displaying an image modal.
 *
 * @param {ImageModalProviderProps} props - The properties for the ImageModalProvider component.
 * @param {JSX.Element} props.children - The child components to be wrapped by the provider.
 *
 * @returns {JSX.Element} The ImageModalProvider component.
 *
 * @remarks
 * This component uses the `useState` hook to manage the modal's open state and the `useRef` hook to store the image source and alt text.
 * It also sets up an event listener for the 'Escape' key to close the modal.
 *
 * @example
 * ```tsx
 * <ImageModalProvider>
 *   <YourComponent />
 * </ImageModalProvider>
 * ```
 *
 * @component
 * @example
 * ```tsx
 * import { ImageModalProvider } from 'path/to/ImageModalProvider';
 *
 * function App() {
 *   return (
 *     <ImageModalProvider>
 *       <YourComponent />
 *     </ImageModalProvider>
 *   );
 * }
 * ```
 */
export function ImageModalProvider({
    children,
}: ImageModalProviderProps): JSX.Element {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const chatService = ChatBlkQueryService.getInstance();
    const appContext = chatService.appContext;
    const imgSrc = useRef<string>('');
    const imgAlt = useRef<string>('');

    useEffect(() => {
        const handleKeyUp = (event: { key: string }) => {
            if (event.key === 'Escape') {
                closeModal();
            }
        };

        window.addEventListener('keyup', handleKeyUp);
        return () => {
            window.removeEventListener('keyup', handleKeyUp);
        };
    }, []);

    const openModal = useCallback(async (config: ImageConfig) => {
        try {
            const imgBlob: Blob = await getStore(appContext)?.get(config.src);
            const reader = new FileReader();
            reader.onload = () => {
                imgSrc.current = reader.result as string;
                imgAlt.current = config.alt;
                setIsModalOpen(true);
            };
            reader.readAsDataURL(imgBlob);
            setIsModalOpen(true);
        } catch (e) {
            console.error('[ChatBLK] Error preloading image: ', e);
        }
    }, []);

    const closeModal = () => {
        setIsModalOpen(false);
    };

    return (
        <ImageModalContext.Provider value={openModal}>
            {isModalOpen && (
                <ServerMessageImageModal
                    className={`server-message-image-modal ${
                        isModalOpen ? 'active' : ''
                    }`}
                    onClick={closeModal}
                >
                    <ExpandedServerMessageImageContainer>
                        <ExpandedServerMessageImage
                            src={imgSrc.current}
                            alt={imgAlt.current}
                            loading={'lazy'}
                        />
                    </ExpandedServerMessageImageContainer>
                </ServerMessageImageModal>
            )}
            {children}
        </ImageModalContext.Provider>
    );
}
